import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography
} from '@mui/material';
import { AVAILABLE_SCOPES, SCOPE_GROUPS, LEGACY_SCOPE_LABELS } from './apiTokenConstants';

/**
 * Grouped scope checkboxes shared by the token creation page and the edit
 * dialog. Groups mirror the product navigation (Workspaces, Goals, Data, ...)
 * and each group header is itself a checkbox that toggles the whole group.
 *
 * `columns` switches the groups from a single column (dialog-friendly) to a
 * responsive grid (page-friendly).
 *
 * Any selected scopes that are no longer in the current vocabulary (legacy
 * scopes on older tokens) are rendered in a trailing "Legacy" section so
 * they are visible and can be removed, never silently dropped.
 */
const ScopePicker = ({ selected, onToggle, onToggleMany, columns = false }) => {
  const grouped = useMemo(
    () =>
      SCOPE_GROUPS
        .map((group) => ({
          group,
          scopes: AVAILABLE_SCOPES.filter((s) => s.group === group)
        }))
        .filter((g) => g.scopes.length > 0),
    []
  );

  const legacySelected = useMemo(() => {
    const known = new Set(AVAILABLE_SCOPES.map((s) => s.value));
    return (selected || []).filter((s) => !known.has(s));
  }, [selected]);

  const renderScopeRow = (scope) => (
    <FormControlLabel
      key={scope.value}
      sx={{ alignItems: 'flex-start', ml: 0.5, mb: 0.25 }}
      control={
        <Checkbox
          size="small"
          sx={{ pt: 0.25 }}
          checked={selected.includes(scope.value)}
          onChange={() => onToggle(scope.value)}
        />
      }
      label={
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
            <Typography variant="body2">{scope.label}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {scope.value}
            </Typography>
          </Box>
          {scope.description && (
            <Typography variant="caption" color="text.secondary" display="block">
              {scope.description}
            </Typography>
          )}
        </Box>
      }
    />
  );

  return (
    <Box
      sx={
        columns
          ? {
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
              columnGap: 3,
              rowGap: 1,
            }
          : undefined
      }
    >
      {grouped.map(({ group, scopes }) => {
        const values = scopes.map((s) => s.value);
        const selectedCount = values.filter((v) => selected.includes(v)).length;
        const allChecked = selectedCount === values.length;

        return (
          <Box key={group} sx={{ mb: 1.5 }}>
            <FormControlLabel
              sx={{ ml: -0.5 }}
              control={
                <Checkbox
                  size="small"
                  checked={allChecked}
                  indeterminate={selectedCount > 0 && !allChecked}
                  onChange={() => onToggleMany(values, !allChecked)}
                />
              }
              label={
                <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
                  {group}
                </Typography>
              }
            />
            <FormGroup sx={{ pl: 2 }}>
              {scopes.map(renderScopeRow)}
            </FormGroup>
          </Box>
        );
      })}

      {legacySelected.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 0.8, color: 'text.secondary' }}>
            Legacy
          </Typography>
          <FormGroup sx={{ pl: 2 }}>
            {legacySelected.map((value) =>
              renderScopeRow({
                value,
                label: LEGACY_SCOPE_LABELS[value] || value,
                description: 'No longer offered - uncheck to remove it from this token'
              })
            )}
          </FormGroup>
        </Box>
      )}
    </Box>
  );
};

export default ScopePicker;
